import { useFocusOnMount } from "../../../../shared/hooks/useFocusOnMount";
import { useSignOutNotice, type SignOutNotice } from "../../../../shared/hooks/useSignOutNotice";
import { useTranslation } from "../i18n/useTranslation";

/**
 * WHY THE PERSON IS SIGNED OUT, said once at the TOP of the content, above
 * the page heading, and focused so it is read first (designer, lura-web-v3
 * "Oct 7 · Sign out (Book)"; copy-1, Founder-approved): "You're signed out."
 * after their own Sign out, "Your session ended. Sign in to continue." after
 * a session end. In memory only (shared/hooks/useSignOutNotice.ts).
 *
 * `only` limits a screen to the line that belongs on it: the service step
 * says only the deliberate one, and leaves a session end's for sign-in.
 */
export function SessionNotice({ only, testId }: { only?: SignOutNotice; testId: string }) {
  return <SessionNoticeLine notice={useSignOutNotice(only)} testId={testId} />;
}

/** The line itself, for a screen that takes the notice from its owner. */
export function SessionNoticeLine({ notice, testId }: { notice: SignOutNotice | null; testId: string }) {
  const { t } = useTranslation();
  // The notice arrives a commit after the screen (taken in an effect), so
  // this runs after the heading's own focus on arrival and takes over.
  const ref = useFocusOnMount<HTMLParagraphElement>({ on: [notice], when: notice !== null });
  if (!notice) return null;
  return (
    <p
      ref={ref}
      className="v3-session-notice"
      role="status"
      tabIndex={-1}
      data-testid={testId}
      data-reason={notice}
    >
      {t(notice === "session-ended" ? "flow.signin.notice.sessionEnded" : "flow.signin.notice.signedOut")}
    </p>
  );
}
